
import { BridgeHandler } from '../mixins/bridge-handler'
import { Component, Mixins } from 'vue-property-decorator'
import { Action, Getter } from 'vuex-class'
import { AppHandler } from '../mixins/app-handler'
import { TranslationHandler } from '../mixins/translation-handler'
import { IFooter, IUser } from '@/types'
import { getUrlParam } from '@/utils/url'
import cms from '@/cms'

@Component({
  components: {},
})
export default class App extends Mixins(AppHandler, BridgeHandler, TranslationHandler) {
  @Getter themePrimaryColor!: string
  @Getter themeFooterIconColor!: string
  @Getter themeIsDark!: string
  @Getter themeText!: string
  @Getter themeLetterSpacing!: number
  @Getter themeRounded!: 'medium' | 'full' | 'none'
  @Getter campaignIsEnded!: boolean
  @Getter userCurrent!: IUser| null
  @Getter campaignSpotifyConnectPageEnabled!: boolean
  @Getter spotifyGateCompleted!: boolean
  @Action('INIT_USER') initUser!: () => Promise<void>

  isLoading = true
  localeKey = ''

  get footerData () {
    return this.localeKey !== undefined ? {
      background: this.themeIsDark ? '#000' : '#fff',
      textColor: this.themeIsDark ? '#fff' : '#000',
      iconColor: this.themeIsDark ? '#000' : '#fff',
      iconBackground: this.themeFooterIconColor,
      isSquareSocial: this.themeRounded === 'none', // sets all square
      links: [
        {
          text: this.$t('footerPrivacyTitle'),
          url: this.$t('footerPrivacy'),
        },
        {
          text: this.$t('footerImprintTitle'),
          url: this.$t('footerImprint'),
        },
        {
          text: this.$t('footerTermsTitle'),
          url: this.$t('footerTerms'),
        },
      ].filter((link) => link.url && link.text),
      socialLinks: [
        { id: 'spotify', url: this.$t('footerSpotify') },
        { id: 'youtube', url: this.$t('footerYoutube') },
        { id: 'instagram', url: this.$t('footerInstagram') },
        { id: 'twitter', url: this.$t('footerTwitter') },
        { id: 'facebook', url: this.$t('footerFacebook') },
        { id: 'apple_music', url: this.$t('footerApple') },
        { id: 'tiktok', url: this.$t('footerTiktok') },
        { id: 'discord', url: this.$t('footerDiscord') },
      ].filter((link) => link.url),
    } : null
  }

  get style () {
    return {
      fontFamily: this.$themeFontFamily,
      color: this.themeText,
      background: this.themeIsDark ? '#000' : '#fff',
      letterSpacing: this.themeLetterSpacing ? `${this.themeLetterSpacing}px` : undefined,
    }
  }

  async created () {
    const callbackParams = new URLSearchParams(window.location.search)
    const callbackService = callbackParams.get('auth_service')
    const isSpotifyCallback = callbackParams.get('state') === 'cms_account_auth' &&
      (!callbackService || callbackService === 'spotify')
    const callbackError = callbackParams.get('error')

    // This init procedure is somewhat complicated because
    // we're not using CMS to handle auth
    // 1. Init CMS without tracking so campaign, translations, etc is loaded
    try {
      await this.$appInit({ editor: false })
    } catch (e) {
      alert(e)
      console.log(e)
    }
    // 2. Check for user login in callback URL or storage and load the current user
    // CMS must be initialized before this so we have access to the campaign ID
    await this.initUser()
    if (this.userCurrent?.service === 'spotify') {
      this.$store.commit('SET_SPOTIFY_GATE_STATUS', 'connected')
    } else if (isSpotifyCallback) {
      this.$store.commit('SET_SPOTIFY_GATE_STATUS', 'error')
    }
    if (this.userCurrent) {
      // We provide the user info to trakcing module before init
      cms.tracking.setTrackingUser({ logged_in: true, email: this.userCurrent.email })
    } else {
      cms.tracking.setTrackingUser(null)
    }

    // 3. Init tracking module which fires the page_meta event (with logged in user info if any)
    cms.tracking.init()

    if (this.campaignSpotifyConnectPageEnabled && isSpotifyCallback) {
      if (this.userCurrent?.service === 'spotify' && !callbackError) {
        cms.tracking.trackCustomer('login_success', {
          method: 'spotify',
          journey: 'spotify_gate',
        })
      } else {
        cms.tracking.trackCustomer('login_fail', {
          method: 'spotify',
          journey: 'spotify_gate',
          error_message: this.spotifyGateErrorCode(callbackError),
        })
      }
    }

    // 4. Event listener to fire virtual_pageview on route changes
    this.$router.afterEach(() => {
      cms.tracking.trackRouteChange()
    })

    if (window.parent) {
      this.$bridgeListenMessages()
    }

    this.isLoading = false

    this.$router.beforeEach((to, _from, next) => {
      const redirect = this.spotifyRouteRedirect(to.name)
      next(redirect || undefined)
    })

    cms.newsletter.initBanner()

    if (this.campaignIsEnded) {
      this.$router.push('/end')
    } else {
      const redirect = this.spotifyRouteRedirect(this.$route.name)
      if (redirect) {
        this.$router.replace(redirect)
      }
    }
  }

  mounted () {
    // Check for newsletter_state=success URL parameter to show success_opt page
    const newsletterState = getUrlParam('newsletter_state')
    if (newsletterState === 'success') {
      this.$router.push('/success_opt')
      return
    }

    if (this.$route.name === 'Success') {
      this.$router.push('/')
    }

    if (this.$route.name === 'SuccessOpt') {
      this.$router.push('/')
    }

    if (!this.campaignIsEnded && this.$route.name === 'End') {
      this.$router.push('/')
    }
  }

  onLocale (locale: string) {
    this.$setLocale(locale)
    this.localeKey = locale
  }

  spotifyRouteRedirect (routeName: string | null | undefined) {
    if (!this.campaignSpotifyConnectPageEnabled) {
      return routeName === 'SpotifyConnect' ? { name: 'Home' } : null
    }

    const completed = this.spotifyGateCompleted
    if (routeName === 'SpotifyConnect') {
      return completed ? { name: 'Home-linked' } : null
    }

    if (!completed && (routeName === 'Home' || routeName === 'Home-linked')) {
      return { name: 'SpotifyConnect' }
    }

    return null
  }

  spotifyGateErrorCode (error: string | null): string {
    const normalized = (error || '').toLowerCase()
    if (normalized.includes('denied') || normalized.includes('cancel')) return 'authorization_denied'
    if (normalized.includes('timeout') || normalized.includes('expired')) return 'authorization_expired'
    if (normalized.includes('state')) return 'state_mismatch'
    return 'provider_error'
  }
}
